import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {makeStyles} from '@material-ui/core';
import {getSeededRandomFunc, getRandomInt} from 'utils/common';

const useStyles = makeStyles(({palette}) => ({
  root: {
    position: 'relative',
  },
  block: {
    position: 'absolute',
    border: '1px solid white',
    background: palette.grey.A100,
  },
  visibleBlock: {
    background: 'transparent',
  },
}));

const AwardImageJigsaw = ({totalReadingDates, award}) => {
  const [{width, height}, setRect] = useState({width: 0, height: 0});
  const classes = useStyles();
  const imageRef = useRef(null);
  const getRandom = useMemo(() => getSeededRandomFunc(award.id), [award]);
  const visibleBlocks = useMemo(() => {
    const nights = totalReadingDates - (award.points - 24);
    const ret = new Array(6).fill(false).map(() => new Array(4).fill(false));
    for (let idx = 0; idx < nights; ++idx) {
      let yIdx, xIdx;
      do {
        xIdx = getRandomInt({max: 3, min: 0, randomFunc: getRandom});
        yIdx = getRandomInt({max: 5, min: 0, randomFunc: getRandom});
      } while (ret[yIdx][xIdx]);
      ret[yIdx][xIdx] = true;
    }
    return ret;
  }, [totalReadingDates, award]);

  useEffect(() => {
    const _handleImageLoad = () => {
      const {width, height} = imageRef.current.getBoundingClientRect();
      setRect({width, height});
    };
    imageRef.current.addEventListener('load', _handleImageLoad);
    return () => {
      imageRef.current.removeEventListener('load', _handleImageLoad);
    };
  }, []);

  const blockWidth = width / 4;
  const blockHeight = height / 6;

  return (
    <div className={classes.root}>
      {new Array(6).fill(0).map((_, yIdx) => (
        <React.Fragment key={yIdx}>
          {new Array(4).fill(0).map((_, xIdx) => (
            <div
              key={xIdx}
              className={clsx(classes.block, {
                [classes.visibleBlock]: visibleBlocks[yIdx][xIdx],
              })}
              style={{
                left: xIdx * blockWidth,
                top: yIdx * blockHeight,
                width: blockWidth,
                height: blockHeight,
              }}
            />
          ))}
        </React.Fragment>
      ))}
      <img
        ref={imageRef}
        src={`${process.env.SERVER_BASE_URL}${award.image}`}
        style={{visibility: width ? 'visible' : 'hidden'}}
      />
    </div>
  );
};

AwardImageJigsaw.propTypes = {
  totalReadingDates: PropTypes.number.isRequired,
  award: PropTypes.object.isRequired,
};

export default AwardImageJigsaw;
